import { ITileSettings } from "../product-collection/manage-tile-view/types";
import * as React from "react";
import { List, Map, Iterable } from "immutable";
import { SCROLL_DIRECTION } from "constants/scroll";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";

// ContainerHOC Definitions
export interface IContainerHOCState {
    isScrolling: boolean;
    scrollTop: number;
    scrollDirectionVertical: SCROLL_DIRECTION;
    scrollingContainer: Element;
    style: React.CSSProperties;
}

export interface IContainerDimensions {
    containerWidth: number;
    containerHeight: number;
}

export interface IContainerHOCProps extends IContainerDimensions {
    api: any;
    children: (params: IContainerHOCState) => React.ReactNode;
}

// End ContainerHOC Definitions

// Match Definitions
export interface IMatchHOCOutsideProps {
    searchTerm?: string;
    searchableFields?: string[][];
    filters?: Map<string, string>;
}

export interface IMatchHOCProps extends IMatchHOCOutsideProps {
    items: List<any>;
    children: (sortDirection: List<any>) => React.ReactNode;
}

// End Match Definitions

export enum GroupKeyType {
    Item = "Item",
    AllocationDimension = "AllocationDimension",
}

export type IGroupKey =
    | {
          key: string[];
          type: GroupKeyType.Item;
      }
    | {
          key: string;
          type: GroupKeyType.AllocationDimension;
      };

// Moveable Definitions
export interface IDragEndEvent {
    currentIndex: number;
    destinationIndex: number;
    rowId: string;
}

export interface IBeforeGroupRenderEvent {
    addChildToRender: (node: React.ReactNode) => void;
    cursor: any;
    groupKeyValues?: List<string>;
    groupKey?: IGroupKey[];
    items: List<any>;
}

export interface IInfiniteLoad {
    isFetching?: boolean;
    onLoadMore?: () => void;
    hasMoreResults?: boolean;
}

export interface IInfiniteLoadingProps extends IInfiniteLoad {
    api: any;
}

type IDisplayFunction = (params: ICellDisplayProps) => IDisplayData;

export interface IMoveableHOCOutsideProps {
    sortable?: boolean;
    itemIdPath: string[];
    onDragEnd?: (event: IDragEndEvent) => void;
    cellRenderer: (props: ICellProps) => React.ReactNode;
    onGetCellDisplayInfo: IDisplayData | IDisplayFunction;
    onClick?: (itemId: string) => void;
}
export interface IMoveableHOCProps extends IMoveableHOCOutsideProps, IContainerDimensions {
    items: List<any>;
    originalItems: List<any>;
    allItems?: List<any>;
    snapshotItems?: List<any>;
    scrollingContainer?: Element;
    api: any;
    visualOffset?: number;
    children: (params: any) => React.ReactNode;
}

export interface IMoveableState {
    isDragging: boolean;
    itemsWithoutDraggingItem?: List<any>;
    draggingItem?: any;
    elementOffset?: number[];
    mousePositionOnPage?: number[];
    mousePositionInScrollContainer?: number[];
    draggingIndex?: number;
    dimensions?: IDisplayData;
    draggingId?: string;
    draggingGroupId?: string;
    scrollTriggerDistance?: number;
    scrollTriggerFastDistance?: number;
}
// End Moveable Definitions

// Grouping Definitions
export interface ISortHOCOutsideProps {
    sortKey?: string[];
    sortDirection?: "asc" | "desc";
    sortType?: "number" | "string";
}
export interface ISortHOCProps extends ISortHOCOutsideProps {
    items: List<any>;
    children: (sortDirection: List<any>) => React.ReactNode;
}
// End Sort Definitions

// Grouping Definitions

export interface IGroupProps {
    groupKeyValues: List<string>;
    groupKey?: IGroupKey[];
    items?: List<IGridItem>;
}

export interface IGroupDisplayProps {
    index: number;
    data: any;
}

interface GroupedValues {
    groupKeyValues: string[];
    items: any[];
}

export interface IGroupHOCReturnParams {
    items: List<any> | null;
    groupKey?: IGroupKey[];
    groupDirection: "asc" | "desc";
    isGrouped: boolean;
    groupedItems?: Iterable<number, GroupedValues>;
}

export interface IGroupHOCOutsideProps {
    groupKey?: IGroupKey[];
    groupDirection?: "asc" | "desc";
    groupRenderer?: (props: IGroupProps) => React.ReactNode;
    onGetGroupDisplayInfo?: IDisplayData | ((IGroupDisplayProps) => IDisplayData);
    comparator?: any;
    groupHeaders?: string[];
}

export interface IGroupHOCProps extends IGroupHOCOutsideProps {
    items: List<any> | null;
    scrollingContainer: Element;
    api: any;
    schema?: List<any> | null;
    children: (params: IGroupHOCReturnParams) => React.ReactNode;
    allocationDimensions?: IAllocationDimension[];
    dimensionFilters: string[][];
    groupItemsOverride: (
        items: List<IGridItem>,
        groupKey?: IGroupKey[],
        groupDirection?: "asc" | "desc"
    ) => IGroupHOCReturnParams;
}

// End Grouping Definitions

// Infinite Base Definitions
export interface IInfiniteBaseProps extends IContainerDimensions {
    api: any;
    cellRenderer: (props: ICellProps) => React.ReactNode;
    onGetCellDisplayInfo: IDisplayData | IDisplayFunction;
    scrollTop: number;
    items: List<any> | null;
    snapshotItems: List<any> | null;
    groupKey?: IGroupKey[];
    groupedItems: Iterable<number, any> | undefined;
    itemIdPath: string[];
    isScrolling: boolean;
    isGrouped: boolean;
    isSortable: boolean;
    draggingId?: string;
    infinite?: boolean;
    centered?: boolean;
    maxWidth: number;
    gutterWidth?: number;
    mousePositionOnPage?: number[];
    tileSettings?: ITileSettings;
    tileViewSize?: string;
}

// End Infinite Base Definitions

// Cell Definitions
export interface ICellProps {
    data: any;
    dataIndex: number;
    isSortable: boolean;
    isGrouped?: boolean;
    groupKeyValues?: List<string>;
}

export interface IDisplayData {
    width?: number;
    height: number;
}

export interface ICellDisplayProps {
    index?: number;
    data: any;
}

// End Cell Definitions

export interface IInfiniteProps
    extends IInfiniteLoad,
        IContainerDimensions,
        IGroupHOCOutsideProps,
        ISortHOCOutsideProps,
        IMatchHOCOutsideProps,
        IMoveableHOCOutsideProps {
    items: List<any> | null;
    allItems?: List<any> | null;
    snapshotItems?: List<any> | null;
    infinite?: boolean;
    centered?: boolean;
    maxWidth?: number;
    gutterWidth?: number;
    schema?: List<any> | null;
    allocationDimensions?: IAllocationDimension[];
    dimensionFilters?: string[][];
    tileSettings?: ITileSettings;
    tileViewSize?: string;
    visualOffset?: number;
    groupItemsOverride?: (items: List<IGridItem>, groupKey?: IGroupKey[], groupDirection?: "asc" | "desc") => any;
}
